import axios from 'axios';
import _ from 'lodash';
import zlib from 'zlib';

import { store } from '../store';
import Constants, { OrderDir } from '../common/constants';
import signatureUtil from './signatureUtil';
import { IsEmpty } from '../common/checks';
import Util from '../common/util';
import { LOGOUT } from '../actions/types';

const API_URL = `${process.env.REACT_APP_API_URL}/v1`;
const STAFF_API = `${process.env.REACT_APP_API_URL}/staff`;

const { CancelToken } = axios;
const cancelRequestList = [];

const createAxiosConfig = ({ signature, timestamp, fileMetadata = null, contentType = Constants.ContentType.ApplicationJson, contentEncoding = null }) => {
  const headers = {
    'Content-Type': contentType,
    'x-auth-token': (store.getState().auth && store.getState().auth[process.env.REACT_APP_TOKEN_NAME] ? store.getState().auth[process.env.REACT_APP_TOKEN_NAME] : ''),
    'x-signature':  signature,
    'x-timestamp':  timestamp,
    'x-filename':   fileMetadata ? encodeURIComponent(fileMetadata.fileName) : '',
    'x-filesize':   fileMetadata ? fileMetadata.fileSize : '',
    'x-filetype':   fileMetadata ? fileMetadata.fileType : '',
    'x-filehash':   fileMetadata ? fileMetadata.fileHash : '',
  };

  if (!IsEmpty(contentEncoding)) {
    headers['Content-Encoding'] = contentEncoding;
  }

  return {
    headers,
    validateStatus(status) { return status < 400; },
    cancelToken: new CancelToken((c) => { cancelRequestList.push(c); }),
    timeout:     45000,
  };
};

const isNetworkError = (err) => {
  const hasError = false;
  const message = String(err.message).toLowerCase();
  if (message.includes('timeout')) {
    throw new Error('The connection has timed out. Server taking too long to respond');
  } else if (message.includes('network')) {
    throw new Error('Unable to connect to server. Please try again later');
  }
  return hasError;
};

const repackageError = (err) => {
  isNetworkError(err);
  const { data, status } = err.response;
  const statusCode = parseInt(status, 10);
  const error = statusCode === 500 ? new Error(Constants.Error.ReportToIT)
    : [400, 401].includes(statusCode) ? new Error(data) : new Error(err.message);
  error.statusCode = statusCode;

  if (statusCode === 500) {
    error.requestId = err?.response.headers?.['x-request-id'] || null;
  } else if ([400, 401].includes(statusCode)) {
    if (data === Constants.Error.SessionExpired) {
      store.dispatch({ type: LOGOUT });
    }
  }
  throw error;
};

const addUrlQueryParams = (url, params) => {
  if (IsEmpty(params) || Object.keys(params).length === 0) {
    return url;
  }

  // NOTE: it is important to we stringify all the dict's values from Object/Integer (except Array) to String
  const paramsCloned = Util.stringifyObjectValuesForUrl(_.cloneDeep(params));

  // form the query params
  const queryParams = new URLSearchParams();
  Object.keys(paramsCloned).sort().forEach((key) => {
    const value = paramsCloned[key];
    if (Array.isArray(value)) {
      value.forEach((v) => {
        queryParams.append(key, String(v));
      });
    } else if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  if (!String(url).includes('?')) {
    url += '?';
  }
  return url + queryString;
};

export const getRequest = async (url = `${STAFF_API}/`, params = {}) => {
  url = addUrlQueryParams(url, params);
  const pathWithQuery = String(url).replace(process.env.REACT_APP_API_URL, '');

  const { signature, timestamp } = await signatureUtil.signRequest({
    base64Key: process.env.REACT_APP_CALL_KEY,
    method:    'GET',
    pathWithQuery,
    bodyObj:   {},
  });
  return axios
    .get(url, createAxiosConfig({ signature, timestamp }))
    .then((res) => res.data)
    .catch((err) => repackageError(err));
};

export const postRequest = async (url = `${STAFF_API}/`, body = {}, file) => {
  const pathWithQuery = String(url).replace(process.env.REACT_APP_API_URL, '');

  let fileMetadata = null;
  if (!IsEmpty(file)) {
    console.log('Hashing file for upload...', file.name);
    const fileHash = await signatureUtil.hashFile(file);
    fileMetadata = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileHash,
    };
  }

  // Create a JSON-safe copy of the object
  // It converts date into valid string. E.g new Date('2025-09-09) -> '2025-09-09T00:00:00.000Z'
  const validJSON = JSON.parse(JSON.stringify(body));

  const { signature, timestamp } = await signatureUtil.signRequest({
    base64Key: process.env.REACT_APP_CALL_KEY,
    method:    'POST',
    pathWithQuery,
    bodyObj:   !IsEmpty(file) ? fileMetadata : validJSON,
  });

  console.log('filemetadata', fileMetadata);
  return axios
    .post(url, body, createAxiosConfig({ signature, timestamp, fileMetadata }))
    .then((res) => res.data)
    .catch((err) => repackageError(err));
};

/**
 *
 * @param {*} url
 * @param {*} body
 * @param {*} options { shouldCompress: boolean }
 * @returns
 */
export const putRequest = async (url = `${STAFF_API}/`, body = {}, file, options = {}) => {
  const pathWithQuery = String(url).replace(process.env.REACT_APP_API_URL, '');

  let fileMetadata = null;
  if (!IsEmpty(file)) {
    const fileHash = await signatureUtil.hashFile(file);
    fileMetadata = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileHash,
    };
  }

  // Create a JSON-safe copy of the object
  const validJSON = JSON.parse(JSON.stringify(body));

  const { signature, timestamp } = await signatureUtil.signRequest({
    base64Key: process.env.REACT_APP_CALL_KEY,
    method:    'PUT',
    pathWithQuery,
    bodyObj:   !IsEmpty(file) ? fileMetadata : validJSON,
  });

  let payload;
  let contentEncoding;
  const { shouldCompress } = options;

  if (shouldCompress && IsEmpty(file)) {
    const json = JSON.stringify(validJSON);
    payload = zlib.gzipSync(json);
    contentEncoding = 'gzip';
  } else {
    payload = body;
  }

  return axios
    .put(url, payload, createAxiosConfig({ signature, timestamp, contentEncoding, fileMetadata }))
    .then((res) => res.data)
    .catch((err) => repackageError(err));
};

export const deleteRequest = async (url = `${STAFF_API}/`, body = {}) => {
  const pathWithQuery = String(url).replace(process.env.REACT_APP_API_URL, '');

  // Create a JSON-safe copy of the object
  // It converts date into valid string. E.g new Date('2025-09-09) -> '2025-09-09T00:00:00.000Z'
  const validJSON = JSON.parse(JSON.stringify(body));

  const { signature, timestamp } = await signatureUtil.signRequest({
    base64Key: process.env.REACT_APP_CALL_KEY,
    method:    'DELETE',
    pathWithQuery,
    bodyObj:   validJSON,
  });

  return axios
    .delete(url, { data: body, ...createAxiosConfig({ signature, timestamp }) })
    .then((res) => res.data)
    .catch((err) => repackageError(err));
};

const login = async ({ email, password }) => postRequest(`${STAFF_API}/auth/login`, { email, password });

const createScrappedPromo = async (data) => postRequest(`${API_URL}/scrappedPromo`, data);

const convertFreeAdToPost = async (id, data) => postRequest(`${STAFF_API}/quickAd/${id}/convertToPost`, { ...data });

const getAddresses = async (params) => getRequest(`${STAFF_API}/addresses`, params);

const getNonGroupPagesHighlights = async (params) => getRequest(`${STAFF_API}/highlights/nonGroupPage`, params);

const getHighlightByPageId = async (pageId) => getRequest(`${STAFF_API}/highlight`, { pageId });
const getMixedHighlights = async () => getRequest(`${STAFF_API}/highlights`);

const getMallOutlets = async (id) => getRequest(`${API_URL}/pages/mallOutlets/${id}`);

const getMalls = async () => getRequest(`${API_URL}/pages/type?type=Mall`);

const getPages = async (name, pageNo, pageSize) => getRequest(`${STAFF_API}/pages?name=${name}&pageNo=${pageNo}&pageSize=${pageSize}`);

const getPagesWithBoundsForMallOrAreaManagement = async (pageType) => getRequest(`${STAFF_API}/mallOrAreaMgmtPagesWithBounds`, { type: pageType });

const getPagesSuggest = (params) => getRequest(`${STAFF_API}/pages/suggest`, params);

const getFreeAds = async (params) => getRequest(`${STAFF_API}/quickAds`, params);

const updateFreeAd = async (id, data) => putRequest(`${STAFF_API}/quickAd/${id}`, { ...data, id });

const getPageById = async (id) => getRequest(`${STAFF_API}/page/${id}`, null);

const uploadPageImage = async (pageId, data, file) => postRequest(`${API_URL}/page/${pageId}/image`, data, file);

const deletePageImage = async (pageId, params) => deleteRequest(`${API_URL}/page/${pageId}/image`, params);

/**
 * Outlets
 */
const getPageOutlets = async (pageId, params = {}) => getRequest(`${STAFF_API}/page/${pageId}/outlets`, params);
const getOutlet = async (outletId) => getRequest(`${STAFF_API}/outlet/${outletId}`);
const updateOutlet = async (outletId, data) => putRequest(`${STAFF_API}/outlet/${outletId}`, data);
const addOutlet = async (data) => postRequest(`${STAFF_API}/outlet`, data);
const deletePageOutlet = async (outletId) => deleteRequest(`${STAFF_API}/outlet/${outletId}`);

/**
 * System Status
 */
const getSystemStatuses = async (params) => getRequest(`${STAFF_API}/systemStatuses`, params);

/**
 * AD
 */
const getAdById = async (id) => getRequest(`${STAFF_API}/ad/${id}`);
const getAds = async (data) => getRequest(`${STAFF_API}/ads`, data);
const updateAd = async (id, data) => putRequest(`${STAFF_API}/ad/${id}`, data);

/**
 * AREA
 */
const getAreas = async () => getRequest(`${STAFF_API}/areas`);
const getAreaById = async (areaId) => getRequest(`${STAFF_API}/area/${areaId}`);
const addArea = async (data) => postRequest(`${STAFF_API}/area`, data);
const updateArea = async (areaId, data) => putRequest(`${STAFF_API}/area/${areaId}`, data);

/**
 * BUSINESS PAGE CLAIMS
 */
const getPageClaims = async (params) => {
  // check orderDir and assign correct value;
  const { orderDir } = params;
  return getRequest(`${STAFF_API}/pageClaims`, {
    ...params,
    orderDir: orderDir ? OrderDir[orderDir] : 'ASC',
  });
};

const getBusinessAccounts = async (params) => getRequest(`${STAFF_API}/businessAccounts`, {
  ...params,
});

const getBusinessStaff = async ({ pageId }) => getRequest(`${STAFF_API}/page/${pageId}/staffs`);

const getBusinessProfile = async (pageId) => getRequest(`${STAFF_API}/page/${pageId}/businessProfile`);

const getPageClaimById = async (pageClaimId) => getRequest(`${STAFF_API}/pageClaim/${pageClaimId}`);

const updatePageClaim = async (pageClaimId, data) => putRequest(`${STAFF_API}/pageClaim/${pageClaimId}`, { ...data });

const getTags = async (params) => {
  const { name, isUsedInPoi, pageNo, pageSize, status, orderDir, trendRank } = params;
  return getRequest(`${STAFF_API}/tags`,
    { name, isUsedInPoi, pageNo, pageSize, status, orderDir, trendRank });
};

/**
 *
 * @param {*} tagNamesDelimited string containing the tag names delimited by double pipes '||'
 * @returns
 */
const getTagsByNames = async (params) => getRequest(`${STAFF_API}/tags/byNames`, params);

const getTagsWithTrendRank = async () => getRequest(`${STAFF_API}/tags/trendRank`);

const updateTagsTrendRank = async (tags) => putRequest(`${STAFF_API}/tags/trendRank`, { tags });

const getPopularTags = async () => getRequest(`${STAFF_API}/tags/popular`);

const getCategorizedPopularTags = async () => getRequest(`${STAFF_API}/tags/popular/categorized`);

const updatePopularTags = async (tags) => putRequest(`${STAFF_API}/tags/popular`, { tags });

const getPopularTagGrouping = async () => getRequest(`${STAFF_API}/popular-tag/grouping`);

const updatePopularTagGrouping = async (data) => putRequest(`${STAFF_API}/popular-tag/grouping`, { data });

const getStaffNames = async () => getRequest(`${STAFF_API}/lastUpdatedBy/names`);

const updateTag = async (data) => putRequest(`${STAFF_API}/tag`, data);

const bulkUpdateTags = async (data) => putRequest(`${STAFF_API}/tags`, data);

const getTagsWithLinkedPOIs = async (params) => getRequest(`${STAFF_API}/tags/linkedPois`, params);

/**
 * NOTE:
 * This endpoint intentionally uses POST instead of GET.
 *
 * Reason:
 * - The request payload contains a potentially large array of tag names.
 * - Using GET would serialize this array into query parameters (?names=a&names=b&...),
 *   which can exceed URL length limits and be truncated or rewritten by browsers,
 *   proxies, load balancers, or WAFs in production.
 * - This caused inconsistent behavior where the backend no longer received the
 *   original request (e.g. parameters being collapsed or morphed).
 **/
const getTagsStatus = (data) => postRequest(`${STAFF_API}/tags/get-status`, data);

/**
 * POST SERVICES
 *
 */
const addPost = async (data) => postRequest(`${STAFF_API}/post`, data);
const getPosts = async (params) => getRequest(`${STAFF_API}/posts`, params);
const getPostById = async (postId) => getRequest(`${STAFF_API}/post/${postId}`);
const getPostsByParamsForPage = async (params, pageId) => getRequest(`${STAFF_API}/page/${pageId}/posts`, params);
const getPostsByParamsForAds = async (params) => getRequest(`${STAFF_API}/ads/posts`, params);
const updatePost = async (postId, data) => putRequest(`${STAFF_API}/post/${postId}`, data, null, { shouldCompress: true });
const deletePost = async (postId) => deleteRequest(`${STAFF_API}/post/${postId}`);
const uploadPostMedia = (postId, data, file) => postRequest(`${STAFF_API}/post/${postId}/media`, data, file);
const deletePostMedia = (postId, params) => deleteRequest(`${STAFF_API}/post/${postId}/media`, params);
/**
 * ANALYTICS SERVICES
 */
const totalSelectionPageViews = async ({ fromDate, toDate }) => getRequest(`${STAFF_API}/analytics/pages?fromDate=${fromDate}&toDate=${toDate}&limit=20`);
const postViewsSelection = async ({ fromDate, toDate }) => getRequest(`${STAFF_API}/analytics/posts?fromDate=${fromDate}&toDate=${toDate}&limit=10`);
const highlightsSelection = async ({ fromDate, toDate }) => getRequest(`${STAFF_API}/analytics/highlights?fromDate=${fromDate}&toDate=${toDate}&limit=10`);
const searchSelection = async ({ fromDate, toDate }) => getRequest(`${STAFF_API}/analytics/search?fromDate=${fromDate}&toDate=${toDate}&limit=10`);
const searchNearbySelection = async ({ fromDate, toDate }) => getRequest(`${STAFF_API}/analytics/nearby?fromDate=${fromDate}&toDate=${toDate}&limit=10`);

/**
 * REVIEWS SERVICE
 */
const getFlaggedReviews = () => getRequest(`${STAFF_API}/reviews/flagged`);

const getReviewsByParams = (data) => getRequest(`${STAFF_API}/reviews`, data);

const getReviewPages = () => getRequest(`${STAFF_API}/reviews/pages`);

const getReview = (id) => getRequest(`${STAFF_API}/review/${id}`);

const getReviewLocationsSuggest = (name) => getRequest(`${STAFF_API}/review/locations/suggest`, { name });

const getReviewLocationsByNames = async (params) => getRequest(`${STAFF_API}/review/locations/byNames`, params);

const addReview = (data) => postRequest(`${STAFF_API}/review`, data);

const updateReview = (id, data) => putRequest(`${STAFF_API}/review/${id}`, data);

const addReviewPlaceLocation = (reviewId, data) => postRequest(`${STAFF_API}/review/${reviewId}/placeLocation`, data);

const updateReviewPlaceLocation = (id, reviewId, data) => putRequest(`${STAFF_API}/review/${reviewId}/placeLocation/${id}`, data);

const uploadReviewMedia = (id, data, file) => postRequest(`${STAFF_API}/review/${id}/media`, data, file);

const deleteReview = (id) => deleteRequest(`${STAFF_API}/review/${id}`);

const deleteReviewPlaceLocation = (id, reviewId, data) => deleteRequest(`${STAFF_API}/review/${reviewId}/placeLocation/${id}`, data);

const deleteReviewMedia = (id, data) => deleteRequest(`${STAFF_API}/review/${id}/media`, data);

const addMobileAppVersion = (data) => postRequest(`${STAFF_API}/mobileAppVersion`, data);

const getMobileAppVersion = (id) => getRequest(`${STAFF_API}/mobileAppVersion/${id}`);

const getMobileAppVersions = (params) => getRequest(`${STAFF_API}/mobileAppVersions`, params);

const updateMobileAppVersion = (id, data) => putRequest(`${STAFF_API}/mobileAppVersion/${id}`, data);

const deleteMobileAppVersion = (id) => deleteRequest(`${STAFF_API}/mobileAppVersion/${id}`);

const clearMobileAppCache = (data) => postRequest(`${STAFF_API}/clearMobileAppCache`, data);

const clearSpecificMobileAppCache = (key) => deleteRequest(`${STAFF_API}/deleteSpecificCache/${key}`);

/**
 * GiveAway Winners
 */
const addGiveAwayWinner = (data) => postRequest(`${STAFF_API}/giveaway/winner`, data);
const getGiveAwayWinners = (params) => getRequest(`${STAFF_API}/giveaway/winners`, params);
const getGiveAwayWinner = (id) => getRequest(`${STAFF_API}/giveaway/winner/${id}`);
const updateGiveAwayWinner = (id, data) => putRequest(`${STAFF_API}/giveaway/winner/${id}`, data);
const deleteGiveAwayWinner = (id) => deleteRequest(`${STAFF_API}/giveaway/winner/${id}`);

const getCategoryPinnedPost = (categoryGroup) => getRequest(`${STAFF_API}/categoryPinnedPost`, { categoryGroup });
const updateCategoryPinnedPostOrderPosition = (categoryGroup, postIds) => putRequest(`${STAFF_API}/categoryPinnedPost/pinnedPostOrder`, { categoryGroup, postIds });
const updateCategoryPinnedPostPositionType = (categoryGroup, postId, positionType) => putRequest(`${STAFF_API}/categoryPinnedPost/pinnedPostPositionType`, { categoryGroup, postId, positionType });
const removeCategoryPinnedPostPositionType = (categoryGroup, postId) => deleteRequest(`${STAFF_API}/categoryPinnedPost`, { categoryGroup, postId });
const getPostsByBrandAndCategoryGroup = (params) => getRequest(`${STAFF_API}/posts/brandAndCategoryGroup`, params);
const addCategoryPinnedPost = (categoryGroup, postId, position, positionType) => postRequest(`${STAFF_API}/categoryPinnedPost`, { categoryGroup, postId, position, positionType });

/**
 * Tags
 */
const getPostsByTag = (params) => getRequest(`${STAFF_API}/tags/posts`, params);
const getReviewsByTag = (params) => getRequest(`${STAFF_API}/tags/reviews`, params);
const getPoisByTag = (params) => getRequest(`${STAFF_API}/tag/pois`, params);

/**
 * MALL DIRECTORY
 */
const getMallDirectoryByMallPageId = (mallPageId) => getRequest(`${STAFF_API}/mallDirectoryByMallPageId/${mallPageId}`);

/**
 * MALL MAP
 */
const getMallMapsByPageId = (params) => getRequest(`${STAFF_API}/mallMapsByPageId`, params);
// Waypoint
const createWaypoint = (data) => postRequest(`${STAFF_API}/waypoint`, data);
const updateWaypoint = (id, data) => putRequest(`${STAFF_API}/waypoint/${id}`, { ...data });
const deleteWaypoint = (id) => deleteRequest(`${STAFF_API}/waypoint/${id}`);
// Edge
const createEdge = (data) => postRequest(`${STAFF_API}/edge`, data);
const deleteEdge = (id) => deleteRequest(`${STAFF_API}/edge/${id}`);

const bulkExecuteMallMapOps = (data) => postRequest(`${STAFF_API}/mallMap/bulk`, data);

/**
 * Map Bound
 */
const getMapBound = (pageId) => getRequest(`${STAFF_API}/page/${pageId}/mapBound`);
const createMapBound = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/mapBound`, data);
const updateMapBound = (pageId, data) => putRequest(`${STAFF_API}/page/${pageId}/mapBound`, data);

const getMapBoundAd = (adId) => getRequest(`${STAFF_API}/ad/${adId}/mapBound`);
const createMapBoundAd = (adId, data) => postRequest(`${STAFF_API}/ad/${adId}/mapBound`, data);
const updateMapBoundAd = (adId, data) => putRequest(`${STAFF_API}/ad/${adId}/mapBound`, data);
const deleteMapBoundAd = (adId) => deleteRequest(`${STAFF_API}/ad/${adId}/mapBound`, { adId });

const getTrailMapBound = (trailId) => getRequest(`${STAFF_API}/trail/${trailId}/mapBound`);
const createTrailMapBound = (trailId, data) => postRequest(`${STAFF_API}/trail/${trailId}/mapBound`, data);
const updateTrailMapBound = (trailId, data) => putRequest(`${STAFF_API}/trail/${trailId}/mapBound`, data);

/**
 * Partner Buildings
 */
const getPartnerBuildings = (pageId) => getRequest(`${STAFF_API}/page/${pageId}/partnerBuildings`);
const createPartnerBuildings = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/partnerBuildings`, data);
const updatePartnerBuildings = (pageId, data) => putRequest(`${STAFF_API}/page/${pageId}/partnerBuildings`, data);

/**
 * PAGE HIGHLIGHT
 */
const getPageHighlights = async (params) => getRequest(`${STAFF_API}/pageHighlights`, params);
const getPageHighlight = async (id) => getRequest(`${STAFF_API}/pageHighlight/${id}`);
const createPageHighlight = async (data) => postRequest(`${STAFF_API}/pageHighlight`, data);
const updatePageHighlight = (pageHighlightId, data) => putRequest(`${STAFF_API}/pageHighlight/${pageHighlightId}`, data);
const uploadPageHighlightMedia = (pageHighlightId, data, file) => postRequest(`${STAFF_API}/pageHighlight/${pageHighlightId}/media`, data, file);
const deletePageHighlightMedia = (pageHighlightId, data) => deleteRequest(`${STAFF_API}/pageHighlight/${pageHighlightId}/media`, data);
const deletePageHighlight = async (pageId, pageHighlightId) => deleteRequest(`${STAFF_API}/pageHighlight`, { pageId, pageHighlightId });

/**
 * SPECIAL HIGHLIGHT - SPECIAL POST
 */
const getPostPageHighlights = (params) => getRequest(`${STAFF_API}/postPageHighlights`, params);
const addPostPageHighlight = (pageId, pageHighlightId, postId) => postRequest(`${STAFF_API}/postPageHighlight`, { pageId, pageHighlightId, postId });
const removePostPageHighlightPositionType = (pageId, pageHighlightId, postId) => deleteRequest(`${STAFF_API}/postPageHighlight`, { pageId, pageHighlightId, postId });
const updatePostPageHighlight = (postPageHighlightId, data) => putRequest(`${STAFF_API}/postPageHighlight/${postPageHighlightId}`, data);

/**
 * SPECIAL HIGHLIGHT - PINNED POST
 */
const getPinnedPosts = (params) => getRequest(`${STAFF_API}/pinnedPosts`, params);
const addPinnedPost = (pageId, pageHighlightId, postPageHighlightId, position, positionType) => postRequest(`${STAFF_API}/pinnedPost`, { pageId, pageHighlightId, postPageHighlightId, position, positionType });
const updatePinnedPostOrderPosition = (pageId, pageHighlightId, postPageHighlightIds) => putRequest(`${STAFF_API}/pinnedPost/pinnedPostOrder`, { pageId, pageHighlightId, postPageHighlightIds });
const updatePinnedPostPositionType = (pageId, pageHighlightId, postPageHighlightId, positionType) => putRequest(`${STAFF_API}/pinnedPost/pinnedPostPositionType`, { pageId, pageHighlightId, postPageHighlightId, positionType });
const removePinnedPostPositionType = (pageId, pageHighlightId, postPageHighlightId) => deleteRequest(`${STAFF_API}/pinnedPost`, { pageId, pageHighlightId, postPageHighlightId });

/**
 * Page Feature Control
 */
const getPageFeature = (pageId) => getRequest(`${STAFF_API}/page/${pageId}/pageFeature`);
const createPageFeature = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/pageFeature`, data);
const updatePageFeature = (pageId, data) => putRequest(`${STAFF_API}/page/${pageId}/pageFeature`, data);

/**
 * Gift
 */
const getGiftsByParams = (pageId, data) => getRequest(`${STAFF_API}/page/${pageId}/gifts`, data);
const getGift = ({ pageId, giftId }) => getRequest(`${STAFF_API}/page/${pageId}/gift/${giftId}`);
const createGift = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/gift`, data);
const updateGift = ({ pageId, giftId }, data) => putRequest(`${STAFF_API}/page/${pageId}/gift/${giftId}`, data);
const deleteGift = ({ pageId, giftId }, data) => deleteRequest(`${STAFF_API}/page/${pageId}/gift/${giftId}`, data);
const uploadGiftMedia = ({ pageId, giftId }, data, file) => postRequest(`${STAFF_API}/page/${pageId}/gift/${giftId}/media`, data, file);
const deleteGiftMedia = ({ pageId, giftId }, data) => deleteRequest(`${STAFF_API}/page/${pageId}/gift/${giftId}/media`, data);

/**
 * Promo Codes
 */
const getPromoCodes = (params) => getRequest(`${STAFF_API}/promoCodes`, params);
const createPromoCodes = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/promoCodes`, data);
const deletePromoCodes = (pageId, data) => deleteRequest(`${STAFF_API}/page/${pageId}/promoCodes`, data);
const updatePromoCode = (pageId, promoCodeId, data) => putRequest(`${STAFF_API}/page/${pageId}/promoCodes/${promoCodeId}`, data);
const bulkUpdatePromoCodes = (pageId, data) => putRequest(`${STAFF_API}/page/${pageId}/promoCodes`, data);

/**
 * Staffs
 */
const getStaffUsernames = () => getRequest(`${STAFF_API}/usernames`);

/**
 * User Moderation
 */
const getBannedShoppers = (params) => getRequest(`${STAFF_API}/shoppers/banned`, params);
const getShopperModerationDetails = (params) => getRequest(`${STAFF_API}/shoppers/moderation/search`, params);
const banShopper = (shopperId, data = {}) => postRequest(`${STAFF_API}/shopper/${shopperId}/moderation/ban`, data);
const unbanShopper = (shopperId, data = {}) => postRequest(`${STAFF_API}/shopper/${shopperId}/moderation/unban`, data);

/**
 * Loyalty Cards
 */
const getLoyaltyCards = (pageId, params) => getRequest(`${STAFF_API}/page/${pageId}/loyaltyCards`, params);
const getLoyaltyCard = (pageId, loyaltyCardId) => getRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}`);
const getStampInfos = (pageId, loyaltyCardId) => getRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/stampInfos`);
const getParticipateOutlets = (pageId, loyaltyCardId, params) => getRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/participatingOutlets`, params);
const createLoyaltyCard = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard`, data);
const updateLoyaltyCard = (pageId, loyaltyCardId, data) => putRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}`, data);
const updateStampInfo = ({ pageId, loyaltyCardId, stampInfoId }, data) => putRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/stampInfo/${stampInfoId}`, data);
const updateLoyaltyCardParticipateOutlets = ({ pageId, loyaltyCardId, outletIds }) => putRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/participatingOutlets`, { outletIds });
const deleteLoyaltyCardParticipateOutlet = ({ pageId, loyaltyCardId, outletId }) => deleteRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/participatingOutlet/${outletId}`);
const deleteLoyaltyCard = (pageId, loyaltyCardId) => deleteRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}`);

const uploadLoyaltyCardMedia = (pageId, loyaltyCardId, data, file) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/media`, data, file);
const updateLoyaltyCardStamps = ({ pageId, loyaltyCardId }, data) => putRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/stampInfos`, data);
const uploadDefaultStampMedia = ({ pageId, loyaltyCardId }, data, file) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/defaultStampMedia`, data, file);
const uploadRedeemStampMedia = ({ pageId, loyaltyCardId }, data, file) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/redeemStampMedia`, data, file);
const uploadStampPrizeMedia = ({ pageId, loyaltyCardId, stampInfoId }, data, file) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/stampInfo/${stampInfoId}/prizeMedia`, data, file);
const uploadStampInfoMedia = ({ pageId, loyaltyCardId, stampInfoId }, data, file) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/stampInfo/${stampInfoId}/media`, data, file);
const uploadStampsHolderMedia = ({ pageId, loyaltyCardId }, data, file) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/stampsHolderMedia`, data, file);

/**
 * RECEIPT UPLOADS SERVICE
 */
const getReceiptUpload = (id) => getRequest(`${STAFF_API}/receiptUpload/${id}`);
const processReceiptUpload = (id, data) => putRequest(`${STAFF_API}/receiptUpload/${id}`, data);

/**
 * Lucky Draws
 */
const getLuckyDraws = (pageId, params) => getRequest(`${STAFF_API}/page/${pageId}/luckyDraws`, params);
const getLuckyDraw = (pageId, luckyDrawId) => getRequest(`${STAFF_API}/page/${pageId}/luckyDraw/${luckyDrawId}`);
const createLuckyDraw = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/luckyDraw`, data);
const uploadLuckyDrawMedia = ({ pageId, luckyDrawId }, data, file) => postRequest(`${STAFF_API}/page/${pageId}/luckyDraw/${luckyDrawId}/media`, data, file);
const updateLuckyDraw = ({ pageId, luckyDrawId }, data) => putRequest(`${STAFF_API}/page/${pageId}/luckyDraw/${luckyDrawId}`, data);
const deleteLuckyDraw = (pageId, luckyDrawId) => deleteRequest(`${STAFF_API}/page/${pageId}/luckyDraw/${luckyDrawId}`);

/**
 * Trails
 */
const getTrails = (pageId, params) => getRequest(`${STAFF_API}/page/${pageId}/trails`, params);
const getTrail = (pageId, trailId) => getRequest(`${STAFF_API}/page/${pageId}/trail/${trailId}`);
const getTrailItems = (pageId, trailId, params = {}) => getRequest(`${STAFF_API}/page/${pageId}/trail/${trailId}/items`, params);
const createTrail = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/trail`, data);
const updateTrail = (pageId, trailId, data) => putRequest(`${STAFF_API}/page/${pageId}/trail/${trailId}`, data);
const updateReviewTrailItems = (pageId, trailId, data) => putRequest(`${STAFF_API}/page/${pageId}/trail/${trailId}/reviews`, data);
const updatePostTrailItems = (pageId, trailId, data) => putRequest(`${STAFF_API}/page/${pageId}/trail/${trailId}/posts`, data);
const deleteTrail = (pageId, trailId) => deleteRequest(`${STAFF_API}/page/${pageId}/trail/${trailId}`);
const deleteTrailItem = (pageId, trailItemId) => deleteRequest(`${STAFF_API}/page/${pageId}/trailItem/${trailItemId}`);

/**
 * FAQs
 */
const getLoyaltyFaqs = (pageId, loyaltyCardId) => getRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/faqs`);
const createLoyaltyFaq = (pageId, loyaltyCardId, data) => postRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/faq`, data);
const updateLoyaltyFaq = (pageId, loyaltyCardId, faqId, data) => putRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/faq/${faqId}`, data);
const updateLoyaltyFaqOrder = (pageId, loyaltyCardId, data) => putRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/faqs`, data);
const deleteLoyaltyFaq = (pageId, loyaltyCardId, faqId) => deleteRequest(`${STAFF_API}/page/${pageId}/loyaltyCard/${loyaltyCardId}/faq/${faqId}`);

/* POIs */
const getPois = (params) => getRequest(`${STAFF_API}/pois`, params);
const suggestPois = (params) => getRequest(`${STAFF_API}/pois/suggest`, params);
const getPoi = (id) => getRequest(`${STAFF_API}/poi/${id}`);
const addPoi = (data) => postRequest(`${STAFF_API}/poi`, data);
const updatePoi = (id, data) => putRequest(`${STAFF_API}/poi/${id}`, data);
const deletePoi = (id) => deleteRequest(`${STAFF_API}/poi/${id}`);
const uploadPoiPhoto = (poiId, data, file) => postRequest(`${STAFF_API}/poi/${poiId}/media`, data, file);
const updatePoiPhoto = (poiId, photoId, data, file) => putRequest(`${STAFF_API}/poi/${poiId}/media/${photoId}`, data, file);
const deletePoiPhoto = (poiId, photoId) => deleteRequest(`${STAFF_API}/poi/${poiId}/media/${photoId}`);

/* Contri POIs */
const getContriPOIByParams = (params) => getRequest(`${STAFF_API}/contriPois`, params);
const getContriPOIById = (id) => getRequest(`${STAFF_API}/contriPoi/${id}`);
const updateContriPOI = (id, data) => putRequest(`${STAFF_API}/contriPoi/${id}`, data);

/* Photos */
const getPhotos = (params) => getRequest(`${STAFF_API}/photos`, params);
const getPhotoDetail = (photoId) => getRequest(`${STAFF_API}/photo/${photoId}`);
const updatePhotoDetail = (photoId, data) => putRequest(`${STAFF_API}/photo/${photoId}`, data);
const bulkProcessPhotos = (data) => putRequest(`${STAFF_API}/photos/bulkProcess`, data);
const getPhotosForPinning = (params) => getRequest(`${STAFF_API}/photos/pinnable`, params);

/* Pinned Photos */
const getPinnedPhotos = (params) => getRequest(`${STAFF_API}/pinnedPhotos`, params);
const updatePinnedPhotos = (data) => putRequest(`${STAFF_API}/pinnedPhotos/bulkProcess`, data);
const deletePageSnapstyrPhoto = (pageId, photoId) => deleteRequest(`${STAFF_API}/page/${pageId}/photo/${photoId}`);
const updatePageSnapstyrPhoto = (pageId, photoId, data) => putRequest(`${STAFF_API}/page/${pageId}/photo/${photoId}`, data);

/** Albums */
const getPageAlbums = (pageId, params = {}) => getRequest(`${STAFF_API}/page/${pageId}/albums`, params);
const getPageAlbum = (pageId, albumId) => getRequest(`${STAFF_API}/page/${pageId}/album/${albumId}`);
const createPageAlbum = (pageId, data) => postRequest(`${STAFF_API}/page/${pageId}/album`, data);
const updatePageAlbum = (pageId, albumId, data) => putRequest(`${STAFF_API}/page/${pageId}/album/${albumId}`, data);
const deletePageAlbum = (pageId, albumId) => deleteRequest(`${STAFF_API}/page/${pageId}/album/${albumId}`);
const uploadPageAlbumPhoto = (pageId, albumId, data, file) => postRequest(`${STAFF_API}/page/${pageId}/album/${albumId}/photo`, data, file);
const updatePageAlbumPhoto = (pageId, albumId, photoId, data, file) => putRequest(`${STAFF_API}/page/${pageId}/album/${albumId}/photo/${photoId}`, data, file);
const deletePageAlbumPhoto = (pageId, albumId, photoId) => deleteRequest(`${STAFF_API}/page/${pageId}/album/${albumId}/photo/${photoId}`);

const logError = async (app, data = {}, environment = 'unknown') => postRequest(`${STAFF_API}/log/error/${app}`, { ...data, environment });

/* Comments */
const getPoiComments = (params) => getRequest(`${STAFF_API}/comments/pois`, params);
const getPoiCommentDetail = (commentId) => getRequest(`${STAFF_API}/comment/${commentId}`);
const updatePoiCommentStatus = (commentId, status) => putRequest(`${STAFF_API}/comment/${commentId}`, { status });
const triggerVulgarityCheck = (commentId) => putRequest(`${STAFF_API}/comment/${commentId}/triggerVulgarityChecker`);

/* Advertisement */
const deleteAdMedia = (id) => deleteRequest(`${API_URL}/sponAd/${id}/media`);

const apiService = {
  login,

  createScrappedPromo,
  clearMobileAppCache,
  convertFreeAdToPost,
  getAdById,
  getAds,
  updateAd,

  getAreas,
  getAreaById,
  addArea,
  updateArea,

  getAddresses,
  getFlaggedReviews,
  getNonGroupPagesHighlights,
  getHighlightByPageId,
  getMixedHighlights,
  getMallOutlets,
  getPagesSuggest,
  getPageClaims,
  getBusinessAccounts,
  getBusinessStaff,
  getBusinessProfile,
  getCategorizedPopularTags,
  getPageClaimById,
  getPageById,
  getStaffNames,
  getMalls,
  getFreeAds,
  getPages,
  getPagesWithBoundsForMallOrAreaManagement,
  getTags,
  getTagsByNames,
  getTagsStatus,
  getPostById,
  getTagsWithTrendRank,
  getTagsWithLinkedPOIs,
  highlightsSelection,
  postViewsSelection,
  searchSelection,
  searchNearbySelection,
  totalSelectionPageViews,
  updateFreeAd,
  updatePageClaim,
  uploadPageImage,
  deletePageImage,
  bulkUpdateTags,
  updateTag,
  updateTagsTrendRank,
  getPopularTags,
  updatePopularTags,
  getPopularTagGrouping,
  updatePopularTagGrouping,
  getReviewsByParams,
  getReviewPages,
  getReview,
  getReviewLocationsSuggest,
  getReviewLocationsByNames,
  addReview,
  updateReview,
  addReviewPlaceLocation,
  updateReviewPlaceLocation,
  uploadReviewMedia,
  deleteReview,
  deleteReviewPlaceLocation,
  deleteReviewMedia,
  addMobileAppVersion,
  getMobileAppVersion,
  getMobileAppVersions,
  updateMobileAppVersion,
  deleteMobileAppVersion,
  clearSpecificMobileAppCache,
  addGiveAwayWinner,
  updateGiveAwayWinner,
  getGiveAwayWinners,
  deleteGiveAwayWinner,
  getGiveAwayWinner,
  getCategoryPinnedPost,
  updateCategoryPinnedPostOrderPosition,
  updateCategoryPinnedPostPositionType,
  removeCategoryPinnedPostPositionType,
  getPostsByBrandAndCategoryGroup,
  addCategoryPinnedPost,
  getPostsByTag,
  getReviewsByTag,
  getMallDirectoryByMallPageId,
  getMallMapsByPageId,
  createWaypoint,
  updateWaypoint,
  deleteWaypoint,
  createEdge,
  deleteEdge,
  bulkExecuteMallMapOps,
  getMapBound,
  createMapBound,
  updateMapBound,
  getMapBoundAd,
  createMapBoundAd,
  updateMapBoundAd,
  deleteMapBoundAd,
  getPartnerBuildings,
  createPartnerBuildings,
  updatePartnerBuildings,

  addPost,
  getPosts,
  updatePost,
  deletePost,
  uploadPostMedia,
  deletePostMedia,

  getPageHighlights,
  getPageHighlight,
  createPageHighlight,
  updatePageHighlight,
  uploadPageHighlightMedia,
  deletePageHighlightMedia,
  deletePageHighlight,

  getPostPageHighlights,
  addPostPageHighlight,
  updatePostPageHighlight,
  removePostPageHighlightPositionType,

  getPinnedPosts,
  getPostsByParamsForPage,
  getPostsByParamsForAds,
  addPinnedPost,
  updatePinnedPostOrderPosition,
  updatePinnedPostPositionType,
  removePinnedPostPositionType,

  getPageFeature,
  createPageFeature,
  updatePageFeature,

  getGiftsByParams,
  getGift,
  createGift,
  updateGift,
  deleteGift,
  uploadGiftMedia,
  deleteGiftMedia,
  getLoyaltyCards,
  getLoyaltyCard,
  getStampInfos,
  getParticipateOutlets,
  createLoyaltyCard,
  updateLoyaltyCard,
  updateLoyaltyCardParticipateOutlets,
  deleteLoyaltyCard,
  uploadLoyaltyCardMedia,
  deleteLoyaltyCardParticipateOutlet,

  updateStampInfo,
  updateLoyaltyCardStamps,
  uploadStampInfoMedia,
  uploadRedeemStampMedia,
  uploadStampPrizeMedia,
  uploadDefaultStampMedia,
  uploadStampsHolderMedia,

  createPromoCodes,
  getPromoCodes,
  deletePromoCodes,
  bulkUpdatePromoCodes,
  updatePromoCode,

  getReceiptUpload,
  processReceiptUpload,

  getLuckyDraws,
  getLuckyDraw,
  createLuckyDraw,
  uploadLuckyDrawMedia,
  updateLuckyDraw,
  deleteLuckyDraw,

  getTrails,
  getTrail,
  getTrailItems,
  createTrail,
  updateTrail,
  updateReviewTrailItems,
  updatePostTrailItems,
  deleteTrail,
  deleteTrailItem,
  getTrailMapBound,
  createTrailMapBound,
  updateTrailMapBound,

  getLoyaltyFaqs,
  createLoyaltyFaq,
  updateLoyaltyFaq,
  updateLoyaltyFaqOrder,
  deleteLoyaltyFaq,

  getSystemStatuses,

  getStaffUsernames,
  getBannedShoppers,
  getShopperModerationDetails,
  banShopper,
  unbanShopper,

  getPois,
  suggestPois,
  getPoi,
  getPoisByTag,
  addPoi,
  updatePoi,
  deletePoi,
  uploadPoiPhoto,
  updatePoiPhoto,
  deletePoiPhoto,

  getContriPOIByParams,
  getContriPOIById,
  updateContriPOI,

  getPageOutlets,
  getOutlet,
  updateOutlet,
  addOutlet,
  deletePageOutlet,

  getPhotos,
  getPhotoDetail,
  updatePhotoDetail,
  bulkProcessPhotos,
  getPhotosForPinning,

  getPinnedPhotos,
  updatePinnedPhotos,

  deletePageSnapstyrPhoto,
  updatePageSnapstyrPhoto,

  getPageAlbums,
  getPageAlbum,
  createPageAlbum,
  updatePageAlbum,
  deletePageAlbum,
  uploadPageAlbumPhoto,
  updatePageAlbumPhoto,
  deletePageAlbumPhoto,

  logError,

  getPoiComments,
  getPoiCommentDetail,
  updatePoiCommentStatus,
  triggerVulgarityCheck,

  deleteAdMedia,
};

export default apiService;
